/**
 * EAA-1736: Shared NodeView root for rich-text media (image / audio / embed).
 *
 * WHAT: wraps a media NodeView in a single <NodeViewWrapper> that
 *  - applies the shared `tiptap-media tiptap-media--{layout}` classes + overlay
 *    positioning,
 *  - for behind/front overlays, renders a drag handle that moves the media by
 *    updating its x/y attrs, and
 *  - provides a right-click context menu to switch layout or remove the media.
 *
 * WHY: image/audio/embed all need the exact same layout + drag + context-menu
 * behaviour. One frame keeps that logic in a single place; each media view only
 * supplies its own inner element (and its own resize handles) as children.
 */

import * as React from 'react';
import { useCallback } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { Move, Play, Trash2, Volume2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from '../../ui/context-menu';
import {
    isOverlayLayout,
    mediaLayoutWrapperClass,
    mediaLayoutWrapperStyle,
    readMediaLayoutState,
} from '../../../lib/tiptap-media-layout';
import type { MediaLayout } from '../../../lib/tiptap-media-layout';
import type { AudioPlayerStyle } from '../../../lib/tiptap-audio';
import { MEDIA_LAYOUT_OPTIONS } from './mediaLayoutOptions';

export interface MediaLayoutFrameProps {
    node: NodeViewProps['node'];
    updateAttributes: NodeViewProps['updateAttributes'];
    deleteNode?: NodeViewProps['deleteNode'];
    selected: boolean;
    /** Type-specific wrapper classes (incl. selected/resizing modifiers). */
    className?: string;
    children: React.ReactNode;
}

export const MediaLayoutFrame: React.FC<MediaLayoutFrameProps> = ({
    node,
    updateAttributes,
    deleteNode,
    selected,
    className,
    children,
}) => {
    const state = readMediaLayoutState(node.attrs as Record<string, unknown>);
    const overlay = isOverlayLayout(state.layout);

    // EAA-1736: a "behind" node (z-index:-1) is unclickable once text covers it.
    // While it is selected, lift it above the text so it stays editable/draggable;
    // the stored layout (and the viewer) keep the true stacking order.
    const wrapperStyle = mediaLayoutWrapperStyle(state);
    if (overlay && selected) {
        wrapperStyle.zIndex = 60;
    }

    const handleDragStart = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const startX = e.clientX;
            const startY = e.clientY;
            const baseX = state.x ?? 0;
            const baseY = state.y ?? 0;

            const onMove = (ev: MouseEvent) => {
                updateAttributes({
                    x: Math.round(baseX + ev.clientX - startX),
                    y: Math.round(baseY + ev.clientY - startY),
                });
            };
            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        [state.x, state.y, updateAttributes]
    );

    const setLayout = useCallback(
        (layout: MediaLayout) => updateAttributes({ layout }),
        [updateAttributes]
    );

    // EAA-1736: audio-only — native controls vs a play-button-only "minimal" style.
    const isAudio = node.type.name === 'audio';
    const currentAudioStyle: AudioPlayerStyle =
        (node.attrs as { style?: AudioPlayerStyle }).style === 'minimal' ? 'minimal' : 'full';
    const setAudioStyle = useCallback(
        (style: AudioPlayerStyle) => updateAttributes({ style }),
        [updateAttributes]
    );

    return (
        <NodeViewWrapper
            as="span"
            className={cn(
                mediaLayoutWrapperClass(state.layout),
                selected && 'tiptap-media--selected',
                className
            )}
            style={wrapperStyle}
            data-layout={state.layout}
        >
            <ContextMenu>
                {/* display:contents trigger — keeps the wrapper's layout intact
                    while still capturing right-clicks anywhere on the media. */}
                <ContextMenuTrigger asChild>
                    <span className="tiptap-media-ctx-trigger">
                        {overlay && selected && (
                            <button
                                type="button"
                                className="tiptap-media-drag-handle"
                                onMouseDown={handleDragStart}
                                aria-label="Move media"
                                title="Drag to move"
                            >
                                <Move className="h-3.5 w-3.5" />
                            </button>
                        )}
                        {children}
                    </span>
                </ContextMenuTrigger>
                <ContextMenuContent className="rich-text-media-context-menu w-48">
                    {MEDIA_LAYOUT_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        const active = state.layout === opt.layout;
                        return (
                            <ContextMenuItem
                                key={opt.layout}
                                onSelect={() => setLayout(opt.layout)}
                                className={cn(active && 'bg-muted/70 font-medium')}
                            >
                                <Icon className="mr-2 h-4 w-4" />
                                {opt.labelFallback}
                            </ContextMenuItem>
                        );
                    })}
                    {isAudio && (
                        <>
                            <ContextMenuSeparator />
                            <ContextMenuItem
                                onSelect={() => setAudioStyle('full')}
                                className={cn(currentAudioStyle === 'full' && 'bg-muted/70 font-medium')}
                            >
                                <Volume2 className="mr-2 h-4 w-4" />
                                Full controls
                            </ContextMenuItem>
                            <ContextMenuItem
                                onSelect={() => setAudioStyle('minimal')}
                                className={cn(currentAudioStyle === 'minimal' && 'bg-muted/70 font-medium')}
                            >
                                <Play className="mr-2 h-4 w-4" />
                                Minimal (play button only)
                            </ContextMenuItem>
                        </>
                    )}
                    {deleteNode && (
                        <>
                            <ContextMenuSeparator />
                            <ContextMenuItem
                                onSelect={() => deleteNode()}
                                className="text-destructive focus:text-destructive"
                            >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Remove
                            </ContextMenuItem>
                        </>
                    )}
                </ContextMenuContent>
            </ContextMenu>
        </NodeViewWrapper>
    );
};

MediaLayoutFrame.displayName = 'MediaLayoutFrame';
